import { useCallback, useMemo } from "react";

import Decimal from "decimal.js";
import { startCase } from "lodash";

import { convertHoldingsBreakdownToPositions } from "@fartherfinance/frontend/api/Investment/helpers/positionUtils";
import {
  IncomingCusipPosition,
  IncomingTickerPosition,
} from "@fartherfinance/frontend/api/Investment/types";
import { AssetClassCategory } from "@fartherfinance/frontend/api/PerformanceGroups/requests/getHoldings";
import useGetTransitionProposalBreakdown from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionProposalBreakdown";
import { TransitionProposalId } from "@fartherfinance/frontend/api/TransitionProposals/types";
import { RequestConfig } from "@fartherfinance/frontend/api/Types";

import { AssetClassification } from "@src/multiCustodian/components/Portfolios/AssetAllocationPieChart/AssetAllocationPieChart";

import { TransitionProposalDetailsData } from "./types";
import useGetTransitionProposalMetrics from "./useGetTransitionProposalMetrics";

export default (auth: RequestConfig | null) => {
  const useGetData = useCallback(
    (
      transitionProposalId: TransitionProposalId
    ): TransitionProposalDetailsData => {
      /**************************************
       * 1. get transition proposal breakdown
       */
      const {
        data: transitionProposalBreakdownData,
        isLoading: isLoadingTransitionProposalBreakdown,
        hasError: hasErrorTransitionProposalBreakdown,
      } = useGetTransitionProposalBreakdown(transitionProposalId, auth);

      /***************************************************
       * 2. get the positions to send to the Finmason API
       *    this is needed to fetch the metrics data
       */
      const positions: (IncomingCusipPosition | IncomingTickerPosition)[] =
        useMemo(() => {
          if (transitionProposalBreakdownData?.data?.holdingsBreakdown) {
            return convertHoldingsBreakdownToPositions(
              transitionProposalBreakdownData.data.holdingsBreakdown
            );
          }
          return [];
        }, [transitionProposalBreakdownData]);

      /********************************
       * 3. get portfolio stats/metrics
       */
      const getTransitionProposalMetrics =
        useGetTransitionProposalMetrics(auth);

      const {
        isLoadingPortfolioStats,
        hasPortfolioStatsError,
        portfolioStats,
        invalidPositions,
      } = getTransitionProposalMetrics(positions);

      /**********************************
       * 4. format the data for the chart
       */
      const classifications: AssetClassification[] = useMemo(() => {
        if (transitionProposalBreakdownData?.data?.typesBreakdown) {
          return transitionProposalBreakdownData.data.typesBreakdown.map(
            (item) => ({
              value: new Decimal(item.weight).times(100).toNumber(),
              label: AssetClassCategory.parse(startCase(item.type)),
            })
          );
        }
        return [];
      }, [transitionProposalBreakdownData]);

      /**********************
       * 5. return everything
       */
      return {
        isLoadingClassifications: isLoadingTransitionProposalBreakdown,

        hasErrorClassifications: hasErrorTransitionProposalBreakdown,

        classifications,

        isLoadingPortfolioStats:
          isLoadingTransitionProposalBreakdown || isLoadingPortfolioStats,

        hasErrorPortfolioStats:
          hasErrorTransitionProposalBreakdown || hasPortfolioStatsError,

        portfolioStats,

        invalidPositions,
      };
    },
    [auth]
  );
  return useGetData;
};
